import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePatientRecord } from '../state/data/patient';
import { useTasksByTemplateCode } from '../state/data/task';
import {
  LAB_NOTE_FIELD_ON, notifyNotesFor, notifySpecFor, resolveNotifyOutcome,
} from '../logic/taskNotify';
import { LAB_NOTE_OUTCOME_COPY, labCardAlreadySent } from '../logic/labs';
import {
  labNoteClean, labNoteName, labNoteOk, LAB_NOTE_LIMITS,
} from '../logic/labResults';
import LabNoteBlock from './LabNoteBlock';

// Pre-send confirmation for the care-team tasks whose COMPLETION messages the
// patient (WF17 texts and emails at once; WF72 posts the lab card later). The send
// is irreversible, so the operator sees what goes out first. Self-contained
// (resolves its own patient + suppression state) so every surface that can
// complete the task gets the same gate.
//
// Three exits: Complete (send) · Cancel — no longer needed (hands off to the cancel
// gate, which owns the patient-task cascade disclosure) · X/Esc/click-outside (no
// state change).
// Destructured in the body, not the signature: in a .jsx file tsc turns a
// defaultless destructured param into a REQUIRED prop, and a `= null` default
// into a null-typed one — either way `error` / `onRequestClose` (both genuinely
// optional) would force casts at the call sites.
export default function CompleteNotifyModal(props) {
  const {
    task, withingsUserId, busy = false, error, onConfirm, onRequestClose, onDismiss,
    author, noteOutcome, noteError, noteFieldOn = LAB_NOTE_FIELD_ON,
  } = props;
  const templateCode = task?.template_code || null;
  const spec = notifySpecFor(templateCode);
  // WF72's notice names no channel or address, so it has nothing to resolve.
  const deferred = spec?.sender === 'wf72';
  // The care team's note (spec 2026-10-14-lab-results-care-team-note §3.8): never once a card went out.
  const noteOffered = !!spec?.noteField && noteFieldOn;
  const cardSent = noteOffered && labCardAlreadySent(task);
  const showNote = noteOffered && !cardSent;
  const [labNote, setLabNote] = useState('');
  // Read by the Escape listener: a typed note survives a stray Escape, as it does a click outside.
  const typedRef = useRef(false);
  const noteId = useId();

  // Authoritative contact = the patients table, the row WF17 itself reads.
  // NOT resolvePatientPhone/Email — their Withings-demographics fallbacks would
  // promise a send WF17 will skip.
  const patientRecord = usePatientRecord(deferred && !noteOffered ? null : withingsUserId);
  const suppression = useTasksByTemplateCode(
    spec?.suppressIfCompleted ? withingsUserId : null,
    spec?.suppressIfCompleted,
  );

  useEffect(() => {
    // CAPTURE + stopPropagation: the patient drawer has its own window-level
    // Escape listener that navigates it closed (router.tsx). Escape here must
    // return the user TO the drawer, not blow past it.
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.isComposing) return;
      e.stopPropagation();
      if (!busy && !typedRef.current) onDismiss();
    };
    window.addEventListener('keydown', onKey, true);
    // Restore whatever the opener had (the drawer locks scroll too) rather than
    // clearing it — clearing would unlock the still-open drawer underneath.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [onDismiss, busy]);

  if (!spec) return null;

  // Measured with the module's own check, never cut: over a limit Complete waits for a shorter note.
  const cleaned = labNoteClean(labNote);
  const noteOver = !!cleaned && !labNoteOk(cleaned);
  const rawName = patientRecord.data?.first_name;
  const patientName = typeof rawName === 'string' && rawName.trim() ? rawName.trim() : 'the patient';
  // After these two, sending again could put a second note in front of the patient: only Close is offered.
  const closeOnly = noteOutcome === 'not_saved' || noteOutcome === 'unconfirmed';

  const resolving = !deferred && (patientRecord.isLoading
    || (!!spec.suppressIfCompleted && suppression.isLoading));

  const outcome = resolveNotifyOutcome(spec, {
    firstName: patientRecord.data?.first_name,
    phone: patientRecord.data?.phone,
    email: patientRecord.data?.email,
    suppressionTasks: suppression.data,
    // An unreadable record or lookup means we cannot promise silence.
    lookupFailed: !!patientRecord.error || !!suppression.error,
  });

  const primaryLabel = resolving
    ? 'Checking…'
    : busy
      ? 'Completing…'
      : deferred || (outcome.sendsNothing && !outcome.lookupFailed)
        ? 'Complete task'
        : 'Complete & notify patient';

  return createPortal((
    <div
      className="modal-overlay"
      style={{ zIndex: 1300 }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy && !labNote.trim()) onDismiss(); }}
    >
      <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="complete-notify-title">
        <button className="modal-close" aria-label="Back to patient" onClick={onDismiss} disabled={busy}>×</button>

        <div className="modal-header">
          <h3 id="complete-notify-title">Complete “{task?.title}”?</h3>
          <p className="body2 text-secondary" style={{ margin: 0 }}>
            {deferred
              ? spec.summary
              : resolving
                ? 'Checking what will be sent…'
                : outcome.suppressed
                  ? `${outcome.firstName} has already completed their booking task, so no message will be sent.`
                  : outcome.noContactOnFile
                    ? `No phone or email on file for ${outcome.firstName} — nothing can be sent.`
                    : (
                      <>
                        This {outcome.willSendSms && outcome.willSendEmail
                          ? 'texts and emails'
                          : outcome.willSendSms ? 'texts' : 'emails'} {outcome.firstName} {outcome.summary} right
                        now. {' '}<b>It cannot be undone.</b>
                      </>
                    )}
          </p>
        </div>

        {!deferred && outcome.lookupFailed && (
          <p className="detail1" style={{ margin: '0 0 14px', color: 'var(--wi-text-primary-moderate)' }}>
            Couldn’t check whether {outcome.firstName} has already booked — a message may still be sent.
          </p>
        )}

        {!deferred && !resolving && !outcome.suppressed && !outcome.noContactOnFile && (
          <p className="detail1 text-secondary" style={{ margin: '0 0 14px' }}>
            {[outcome.willSendSms ? `Text to ${patientRecord.data?.phone}` : 'No phone on file — no text',
              outcome.willSendEmail ? `Email to ${patientRecord.data?.email}` : 'No email on file — no email',
            ].join(' · ')}
          </p>
        )}

        {notifyNotesFor(spec, task).map((line) => (
          <p key={line} className="detail1" style={{ margin: '0 0 14px', color: 'var(--wi-text-primary-moderate)' }}>{line}</p>
        ))}

        {showNote && (
          <div className="lab-note-field">
            <label htmlFor={noteId} className="body2 lab-note-field__label">Note for {patientName} (optional)</label>
            <textarea
              id={noteId}
              className="rx-input"
              rows={4}
              value={labNote}
              onChange={(e) => { setLabNote(e.target.value); typedRef.current = !!e.target.value.trim(); }}
              disabled={busy}
              readOnly={closeOnly}
              aria-describedby={`${noteId}-meta`}
              aria-invalid={noteOver || undefined}
            />
            <div id={`${noteId}-meta`} className="lab-note-field__meta detail1">
              <span className="text-secondary">Shown at the top of the results card, signed with your first name. Plain text: links are not clickable.</span>
              <span className={noteOver ? 'lab-note-field__count is-over' : 'lab-note-field__count'}>
                {cleaned.length} / {LAB_NOTE_LIMITS.chars}
              </span>
            </div>
            {noteOver && (
              <p role="alert" className="detail1 lab-note-field__error">
                Too long: at most {LAB_NOTE_LIMITS.chars} characters and {LAB_NOTE_LIMITS.lines} lines. Nothing is cut, so shorten the note.
              </p>
            )}
            {cleaned && !noteOver && (
              <div>
                <p className="eyebrow text-secondary lab-note-field__preview-label">Preview</p>
                <LabNoteBlock name={labNoteName(author?.firstname)} text={cleaned} />
              </div>
            )}
          </div>
        )}

        {cardSent && (
          <p className="detail1 text-secondary" style={{ margin: '0 0 14px' }}>
            The results were already shared. To add a note, write to {patientName} in the chat.
          </p>
        )}

        {noteOutcome && (
          <p role="alert" className="detail1" style={{ margin: '0 0 14px', color: 'var(--wi-text-primary-moderate)' }}>
            {LAB_NOTE_OUTCOME_COPY[noteOutcome] ?? LAB_NOTE_OUTCOME_COPY.not_done}
            {noteError ? ` (${noteError})` : ''}
          </p>
        )}

        {outcome.alsoCreates.length > 0 && (
          <p className="detail1 text-secondary" style={{ margin: '0 0 14px' }}>
            Also creates: {outcome.alsoCreates.join(', ')}.
          </p>
        )}

        {error && (
          <p className="detail1" style={{ margin: '0 0 14px', color: 'var(--wi-status-bad)' }}>
            {error} — nothing was sent.
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: onRequestClose ? 'space-between' : 'flex-end', alignItems: 'center' }}>
          {/* Only offered where the caller owns a cancel flow (the patient file's
              cancel gate). Rendering a no-op button on a modal whose job is to
              make consequences legible would be its own small lie. */}
          {onRequestClose && !closeOnly && (
            <button
              className="btn-secondary"
              style={{ fontSize: 13, padding: '8px 16px' }}
              onClick={onRequestClose}
              disabled={busy}
            >
              {spec.cancelLabel || 'Cancel — no longer needed'}
            </button>
          )}
          <button
            className="btn-primary"
            style={{ fontSize: 13, padding: '8px 16px' }}
            onClick={closeOnly ? onDismiss : () => onConfirm(showNote && cleaned ? cleaned : undefined)}
            disabled={busy || resolving || (showNote && noteOver)}
          >
            {closeOnly ? 'Close' : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
